import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  GuestInvitationService,
  GuestInvitation,
} from "@/services/guestInvitationService";
import { useSupabaseAuth, useAuthModal } from "@/context/SupabaseAuthContext";
import {
  CheckCircle,
  XCircle,
  UserPlus,
  Clock,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useSupabaseAuth();
  const { setShowLogin } = useAuthModal();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [invitation, setInvitation] = useState<GuestInvitation | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    // Always load invitation details (now works for unauthenticated users)
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const result = await GuestInvitationService.getInvitationByToken(token!);

      if (result.success && result.invitation) {
        setInvitation(result.invitation);

        // Resolve project name if a project_id is present
        if (result.invitation.project_id) {
          const { data: project } = await supabase
            .from("conservation_projects")
            .select("name")
            .eq("id", result.invitation.project_id)
            .maybeSingle();
          setProjectName(project?.name ?? null);
        }

        // Check if invitation is expired
        const expiresAt = new Date(result.invitation.expires_at);
        const now = new Date();

        if (expiresAt < now) {
          setError("This invitation has expired");
        } else if (result.invitation.status !== "pending") {
          setError(
            `This invitation has already been ${result.invitation.status}`,
          );
        }
      } else {
        setError(result.error || "Invitation not found");
      }
    } catch (error) {
      setError("Failed to load invitation details");
    } finally {
      setLoading(false);
    }
  };

  // Handle authentication state changes - auto-reload invitation when user signs in
  useEffect(() => {
    if (user && invitation && invitation.status === "pending") {
      // User just signed in and invitation is still pending
      // We could optionally auto-accept here, but for now just reload to show authenticated UI
      loadInvitation();
    }
  }, [user]);

  const handleAccept = async () => {
    if (!invitation || !user) return;

    setAccepting(true);
    try {
      // Accept + bill host (1 credit) server-side.
      const { data: json, error } = await supabase.functions.invoke(
        "accept-guest-invitation",
        { body: { invitation_id: invitation.id } },
      );

      if (error || !json?.success) {
        const code = (json as any)?.error || error?.message;
        toast({
          title: "Failed to Accept",
          description:
            code === "insufficient_credits"
              ? "The inviter doesn't have enough credits to accept this invite right now. Please ask them to top up and resend."
              : (json as any)?.error ||
                error?.message ||
                "Failed to accept invitation",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Invitation Accepted",
        description:
          "You've successfully joined the project collaboration. Redirecting to your projects...",
        variant: "default",
      });

      // Redirect to projects page where guest can see shared projects
      navigate("/projects");
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation) return;

    setDeclining(true);
    try {
      const result = await GuestInvitationService.declineInvitation(
        invitation.id,
      );

      if (result.success) {
        toast({
          title: "Invitation Declined",
          description: "You've declined the project collaboration.",
          variant: "default",
        });

        navigate("/");
      } else {
        toast({
          title: "Failed to Decline",
          description: result.error || "Failed to decline invitation",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2">Loading invitation...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
            <CardTitle className="text-red-700">Invalid Invitation</CardTitle>
            <CardDescription className="text-red-600">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate("/")}
              className="w-full"
              variant="outline"
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user && invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <UserPlus className="h-12 w-12 text-blue-500 mx-auto mb-2" />
            <CardTitle>Join Project Collaboration</CardTitle>
            <CardDescription>
              You've been invited to collaborate on a project
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Invitation Details */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-600">
                  Invited Email:
                </span>
                <span className="text-sm font-mono bg-white px-2 py-1 rounded border text-gray-600">
                  {invitation.guest_email}
                </span>
              </div>

              {invitation.project_id && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Project:</span>
                  <Badge variant="secondary">
                    {projectName ?? invitation.project_id}
                  </Badge>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-600">Permissions:</span>
                <div className="flex gap-1">
                  {invitation.permissions?.view && (
                    <Badge variant="outline" className="text-xs text-gray-600">
                      View
                    </Badge>
                  )}
                  {invitation.permissions?.edit && (
                    <Badge variant="outline" className="text-xs text-gray-600">
                      Edit
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-600">Expires:</span>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Clock className="h-3 w-3" />
                  {new Date(invitation.expires_at).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600">
                To accept this invitation, you need to sign in or create an
                account
              </p>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    // Store redirect info before opening modal
                    sessionStorage.setItem(
                      "redirectAfterAuth",
                      window.location.pathname + window.location.search,
                    );
                    setShowLogin(true);
                  }}
                  className="flex-1"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => {
                    // Store redirect info before opening modal
                    sessionStorage.setItem(
                      "redirectAfterAuth",
                      window.location.pathname + window.location.search,
                    );
                    setShowLogin(true);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Create Account
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <UserPlus className="h-12 w-12 text-blue-500 mx-auto mb-2" />
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              You need to sign in to accept this invitation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => {
                // Store redirect info before opening modal
                sessionStorage.setItem(
                  "redirectAfterAuth",
                  window.location.pathname + window.location.search,
                );
                setShowLogin(true);
              }}
              className="w-full"
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <UserPlus className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <CardTitle className="text-2xl">
            Project Collaboration Invitation
          </CardTitle>
          <CardDescription>
            You've been invited to collaborate on an environmental project
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-600">Project:</span>
              <Badge variant="secondary">
                {projectName ?? invitation?.project_id ?? "Unknown Project"}
              </Badge>
            </div>

            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-600">Invited Email:</span>
              <span className="text-sm font-medium text-gray-600">
                {invitation?.guest_email}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-600">Permissions:</span>
              <div className="flex gap-1 ">
                {invitation?.permissions?.view && (
                  <Badge
                    variant="outline"
                    className="text-xs font-medium text-gray-600"
                  >
                    View
                  </Badge>
                )}
                {invitation?.permissions?.edit && (
                  <Badge
                    variant="outline"
                    className="text-xs font-medium text-gray-600"
                  >
                    Edit
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-600">Expires:</span>
              <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
                <Clock className="h-3 w-3" />
                {invitation &&
                  new Date(invitation.expires_at).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Info note if signed-in email differs from invited email */}
          {user.email?.toLowerCase() !==
            invitation?.guest_email?.toLowerCase() && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Invitation sent to <strong>{invitation?.guest_email}</strong>.
                  You're accepting as <strong>{user.email}</strong>.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleAccept}
              disabled={accepting || declining}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {accepting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accept Invitation
                </>
              )}
            </Button>

            <Button
              onClick={handleDecline}
              disabled={accepting || declining}
              variant="outline"
              className="flex-1"
            >
              {declining ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Declining...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Decline
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
